import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ZoomIn } from 'lucide-react';
import { AnimatePresence } from 'framer-motion';
import { REFERENCES, SERVICES } from '../mock/data';
import { useT } from '../i18n/LanguageContext';
import { SERVICE_TRANSLATIONS } from '../i18n/translations';
import { Reveal } from '../hooks/useReveal';
import Lightbox from '../components/Lightbox';
import MagneticButton from '../components/MagneticButton';
import SEO, { breadcrumbLd } from '../components/SEO';

const ReferencesPage = () => {
  const { t, lang } = useT();
  const categories = [
    { id: 'all', label: t('refs.filterAll') },
    ...SERVICES.map((s) => ({ id: s.id, label: SERVICE_TRANSLATIONS[s.id]?.[lang]?.short })),
  ];
  const [filter, setFilter] = useState('all');
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const filtered = filter === 'all' ? REFERENCES : REFERENCES.filter((r) => r.category === filter);

  return (
    <div className="bg-white pt-32 pb-24 relative overflow-hidden">
      <SEO
        title="Referenzen · Bilder & Impressionen aus Seelisberg und Uri"
        description="Aktuelle Projekte von Frachigers Handwerk: Bruchsteinmauern, Plattenverlegungen, Naturstein und Ab-Hof-Impressionen aus Seelisberg, Uri und der Zentralschweiz."
        path="/referenzen"
        lang={lang}
        jsonLd={[breadcrumbLd([{ name: 'Home', path: '/' }, { name: 'Referenzen', path: '/referenzen' }])]}
      />
      {/* Ambient decoration */}
      <div className="absolute top-40 -right-32 w-[500px] h-[500px] bg-brand-blueMist/60 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute top-96 -left-32 w-[500px] h-[500px] bg-brand-blueLight/10 rounded-full blur-3xl pointer-events-none"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="text-brand-blueLight uppercase tracking-[0.3em] text-xs mb-3 font-bold">{t('refs.eyebrow')}</div>
            <h1 className="font-display text-5xl md:text-7xl text-brand-blueDark leading-tight mb-6 tracking-tight">
              <span className="text-brand-blue uppercase">{t('refs.title1')}</span>
              <br />
              <span className="italic">{t('refs.title2')}</span>
            </h1>
            <p className="text-brand-ink/70 text-lg leading-relaxed">{t('refs.subtitle')}</p>
            <p className="text-brand-blueLight text-sm mt-3 uppercase tracking-widest font-bold">Klicken · Blättern · Bewundern</p>
          </div>
        </Reveal>

        {/* Filter chips */}
        <div className="flex flex-wrap justify-center gap-2 mb-12" data-testid="filter-chips">
          {categories.map((c) => (
            <button
              key={c.id}
              onClick={() => setFilter(c.id)}
              className={`px-5 py-2.5 text-xs uppercase tracking-wider font-bold rounded-full transition-all border-2 ${
                filter === c.id
                  ? 'bg-brand-blueDark text-white border-brand-blueDark shadow-lg shadow-brand-blue/30 scale-105'
                  : 'bg-white text-brand-blueDark border-brand-blueMist hover:border-brand-blue'
              }`}
              data-testid={`filter-${c.id}`}
            >
              {c.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((r, i) => (
            <Reveal key={r.id} delay={i * 50}>
              <button
                onClick={() => setLightboxIndex(i)}
                className="group relative block w-full text-left bg-white rounded-md overflow-hidden shadow-sm hover:shadow-2xl hover:shadow-brand-blue/15 border border-brand-blueMist hover:border-brand-blue transition-all lift-on-hover"
                data-testid={`ref-card-${r.id}`}
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-brand-beige">
                  <img
                    src={r.image}
                    alt={r.titleKey}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000 ease-out"
                  />
                  <div className="absolute inset-0 bg-brand-blueDark/0 group-hover:bg-brand-blueDark/50 transition-colors flex items-center justify-center">
                    <div className="w-14 h-14 rounded-full bg-white text-brand-blueDark flex items-center justify-center opacity-0 group-hover:opacity-100 scale-75 group-hover:scale-100 transition-all shadow-2xl">
                      <ZoomIn className="w-6 h-6" />
                    </div>
                  </div>
                  <div className="absolute top-3 right-3 bg-white text-brand-blueDark text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full shadow-md">
                    {SERVICE_TRANSLATIONS[r.category]?.[lang]?.short}
                  </div>
                </div>
                {/* Solid white readable caption */}
                <div className="p-5">
                  <h3 className="font-display text-lg text-brand-blueDark leading-tight mb-1">{r.titleKey}</h3>
                  <div className="flex items-center gap-2">
                    <div className="h-px w-6 bg-brand-blueLight"></div>
                    <p className="text-brand-blueLight text-xs uppercase tracking-widest font-bold">{r.location}</p>
                  </div>
                </div>
              </button>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="mt-16 bg-brand-beige border-2 border-dashed border-brand-blueLight rounded-md p-10 text-center relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-40 h-40 bg-brand-blueLight/20 rounded-full blur-2xl"></div>
            <div className="relative">
              <div className="font-display text-2xl md:text-3xl text-brand-blueDark mb-2">{t('refs.comingSoon')}</div>
              <p className="text-brand-ink/70 text-sm mb-6 max-w-xl mx-auto">{t('refs.comingSoonText')}</p>
              <MagneticButton>
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 bg-brand-blueDark hover:bg-brand-blue text-white px-8 py-4 text-sm font-bold uppercase tracking-wider rounded-sm transition-colors btn-pulse-blue"
                  data-testid="refs-cta"
                >
                  {t('refs.requestOwn')}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </MagneticButton>
            </div>
          </div>
        </Reveal>
      </div>

      <AnimatePresence>
        {lightboxIndex !== null && (
          <Lightbox
            images={filtered}
            index={lightboxIndex}
            onClose={() => setLightboxIndex(null)}
            onIndexChange={setLightboxIndex}
            subtitle={`${SERVICE_TRANSLATIONS[filtered[lightboxIndex]?.category]?.[lang]?.short} · ${filtered[lightboxIndex]?.location}`}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ReferencesPage;
